import {
  __,
  always,
  anyPass,
  applySpec,
  equals,
  find,
  gte,
  mergeDeepRight,
  not,
  path,
  pathOr,
  pluck,
  pipe,
  prop,
  propEq,
  propOr,
  when,
} from 'ramda'

import {
  ACCOUNT_RECEIVE,
  CLEAR_LOGIN_ERRORS,
  COMPANY_RECEIVE,
  FEE_PRESET_RECEIVE,
  LOGIN_FAIL,
  LOGIN_RECEIVE,
  LOGIN_REQUEST,
  LOGOUT_RECEIVE,
  RECIPIENT_RECEIVE,
  RECIPIENT_BALANCE_RECEIVE,
  GET_ACQUIRERS_RESPONSE,
  UPDATE_AUTH,
  UPDATE_COMPANY,
  CHANGE_STATUS_LOADING,
} from './actions'

const getBalance = applySpec({
  available: path(['withdrawal', 'maximum']),
  waitingFunds: path(['balance', 'waiting_funds', 'amount']),
})

const initialState = {
  accountId: null,
  companyId: null,
  defaultRecipient: {},
  hasCreditLines: false,
  hasExternalContract: false,
  hasLoan: false,
  impersonationEnv: sessionStorage.getItem('impersonationEnv') || null,
  impersonationKey: null,
  jwt: null,
  loading: false,
  merchantId: null,
  refreshToken: null,
  sessionId: null,
}

export default function loginReducer (state = initialState, action) {
  switch (action.type) {
    case ACCOUNT_RECEIVE: {
      return mergeDeepRight(state, {
        error: null,
        user: action.payload,
      })
    }

    case COMPANY_RECEIVE: {
      return mergeDeepRight(state, {
        company: action.payload,
        error: null,
        loading: false,
      })
    }

    case UPDATE_AUTH: {
      return {
        ...state,
        ...action.payload,
      }
    }

    case UPDATE_COMPANY: {
      return mergeDeepRight(state, {
        company: action.payload,
      })
    }

    case LOGIN_REQUEST: {
      return mergeDeepRight(state, {
        accountId: null,
        company: null,
        error: null,
        impersonationEnv: action.payload?.impersonationEnv ?? null,
        impersonationKey: action.payload?.impersonationKey ?? null,
        jwt: null,
        loading: true,
        merchantId: null,
        refreshToken: action.payload?.refreshToken ?? null,
        user: null,
      })
    }

    case LOGIN_FAIL: {
      return mergeDeepRight(
        state,
        {
          accountId: null,
          client: null,
          companyId: null,
          error: action.payload,
          impersonationEnv: null,
          impersonationKey: null,
          jwt: null,
          loading: false,
          merchantId: null,
          sessionId: null,
          user: null,
        }
      )
    }
    case LOGIN_RECEIVE: {
      return {
        ...state,
        accountId: propOr(null, 'account_id', action.payload.authentication),
        client: action.payload,
        companyId: propOr(null, 'company_id', action.payload.authentication),
        error: null,
        jwt: propOr(null, 'jwt', action.payload.authentication),
        loading: false,
        merchantId: propOr(null, 'merchant_id', action.payload.authentication),
        sessionId: propOr(null, 'session_id', action.payload.authentication),
      }
    }

    case CLEAR_LOGIN_ERRORS: {
      return mergeDeepRight(
        state,
        {
          error: null,
        }
      )
    }

    case CHANGE_STATUS_LOADING: {
      return mergeDeepRight(
        state,
        { loading: action.payload.loading }
      )
    }

    case LOGOUT_RECEIVE: {
      return initialState
    }

    case RECIPIENT_BALANCE_RECEIVE: {
      return mergeDeepRight(
        state,
        {
          balance: getBalance(action.payload),
        }
      )
    }

    case RECIPIENT_RECEIVE: {
      return mergeDeepRight(
        state,
        {
          defaultRecipient: action.payload,
        }
      )
    }

    case FEE_PRESET_RECEIVE: {
      return mergeDeepRight(
        state,
        {
          defaultRecipient: {
            ...state.defaultRecipient,
            feePreset: action.payload,
          },
        }
      )
    }

    case GET_ACQUIRERS_RESPONSE: {
      return mergeDeepRight(state, {
        acquirers: action.payload,
      })
    }

    default:
      return state
  }
}

const getAntifraudCost = pipe(
  pathOr([], ['gateway', 'live', 'antifraud_cost']),
  find(propEq('name', 'pagarme')),
  prop('cost')
)

const notDefaultInstallments = pipe(
  pluck('installment'),
  anyPass([equals([1, 2, 7]), equals([1])]),
  not
)

const CREDIT_CARD_MDRS_TYPES = [null, 'credit_card']
const findCreditCardMDR = mdrs => mdrs.find(({
  payment_method: paymentMethod,
}) => CREDIT_CARD_MDRS_TYPES.includes(paymentMethod))

const getInstallments = pipe(
  findCreditCardMDR,
  pathOr([], ['installments']),
  when(notDefaultInstallments, always([]))
)

const getFees = pipe(
  prop('pricing'),
  applySpec({
    anticipation: path(['psp', 'live', 'anticipation']),
    antifraud: getAntifraudCost,
    boleto: path(['gateway', 'live', 'boletos', 'payment_fixed_fee']),
    gateway: path(['gateway', 'live', 'transaction_cost', 'credit_card']),
    installments: pipe(
      pathOr([], ['psp', 'live', 'mdrs']),
      getInstallments
    ),
    pix: applySpec({
      paymentFixedFee: path(['gateway', 'live', 'pix', 'payment_fixed_fee']),
      paymentSpreadFee: path(['gateway', 'live', 'pix', 'payment_spread_fee']),
    }),
    transfer: path(['transfers', 'ted']),
  })
)

export const selectCompanyFees = ({ company, defaultRecipient = {} }) => {
  const companyFees = getFees(company)

  if (!defaultRecipient.feePreset) {
    return companyFees
  }

  if (defaultRecipient.anticipation_type === 'compulsory') {
    companyFees.anticipation = defaultRecipient.feePreset.anticipation_rate
  }

  return {
    ...companyFees,
    installments: getInstallments(defaultRecipient.feePreset.mdrs),
  }
}

// The value 1_000_000 is set by the Risk Team on the property
// `company.transfer.blocked_balance_amount` when the company is
// not allowed to do withdraws.
const BLOCKED_AMOUNT_LIMIT = 100000000

export const selectHasBlockedWithdraw = pipe(
  pathOr(0, ['transfers', 'blocked_balance_amount']),
  gte(__, BLOCKED_AMOUNT_LIMIT)
)

export const selectAnticipationType = ({ company, defaultRecipient }) => {
  if (company?.capabilities.allow_transaction_anticipation) {
    return 'compulsory'
  }

  return defaultRecipient?.automatic_anticipation_type
}
